import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ClipboardList, Plus, ChevronRight, CheckCircle2, AlertCircle, Clock,
  Circle, ArrowRight, X, Send, Activity, FileText, Bell, ShieldAlert,
} from 'lucide-react';
import { logger } from './utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RAG_COLOR = {
  green: { bg: 'bg-emerald-100', text: 'text-emerald-800', dot: 'bg-emerald-500' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-800', dot: 'bg-amber-500' },
  red: { bg: 'bg-rose-100', text: 'text-rose-800', dot: 'bg-rose-500' },
  grey: { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
};

const SEVERITY_COLOR = {
  P0: 'bg-rose-100 text-rose-800',
  P1: 'bg-amber-100 text-amber-800',
  P2: 'bg-blue-100 text-blue-800',
  P3: 'bg-slate-100 text-slate-600',
};

const TASK_STATUS_COLOR = {
  todo: 'bg-slate-100 text-slate-700',
  in_progress: 'bg-blue-100 text-blue-800',
  blocked: 'bg-rose-100 text-rose-800',
  done: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-slate-200 text-slate-500 line-through',
};

// ===========================================================================
// LIST — Admin or client landing
// ===========================================================================
export function PMProjectList({ adminMode }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/pm/projects`);
      setProjects(data || []);
    } catch (err) { logger.error('pm list', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold flex items-center gap-2" data-testid="pm-list-title">
            <ClipboardList className="w-5 h-5 text-blue-600" /> Implementation Projects
          </h1>
          {adminMode && (
            <div className="flex items-center gap-2">
              <Link to="/admin/pm/inbox" data-testid="pm-portfolio-inbox-link"
                className="px-3 py-2 border border-blue-300 text-blue-700 hover:bg-blue-50 text-sm rounded inline-flex items-center gap-1">
                Portfolio inbox
              </Link>
              <button onClick={() => setShowNew(true)} data-testid="pm-new-project-btn"
                className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded inline-flex items-center gap-1">
                <Plus className="w-4 h-4" /> New project
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {showNew && <NewProjectModal onClose={() => setShowNew(false)} onCreated={(id) => { setShowNew(false); navigate(`${adminMode ? '/admin' : ''}/pm/${id}`); }} />}

        {loading ? <p className="text-slate-500">Loading…</p> :
          projects.length === 0 ? <p className="text-center text-slate-400 py-16">{adminMode ? 'No projects yet. Click "New project" to start.' : 'No active implementation projects assigned to you.'}</p> :
          <ul className="grid md:grid-cols-2 gap-4">
            {projects.map((p) => (
              <li key={p.id} data-testid={`pm-project-${p.id}`}>
                <Link to={`${adminMode ? '/admin' : ''}/pm/${p.id}`} className="block bg-white border rounded-lg p-4 hover:shadow-md transition">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold">{p.customer_org}</p>
                      <p className="text-xs text-slate-500 mt-0.5 capitalize">{p.methodology} · {p.status}</p>
                    </div>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{p.phase_count} phases</span>
                  </div>
                  <p className="text-sm text-slate-600">Current phase: <strong>{p.current_phase_name || '—'}</strong></p>
                  {p.target_go_live && <p className="text-xs text-slate-500 mt-1">Target go-live: {p.target_go_live}</p>}
                </Link>
              </li>
            ))}
          </ul>
        }
      </main>
    </div>
  );
}

function NewProjectModal({ onClose, onCreated }) {
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({
    customer_org: '', client_owner_email: '', client_owner_name: '',
    vh_owner_email: '', vh_owner_name: '', methodology: 'wagile',
    target_go_live: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/pm/templates`).then((r) => setTemplates(r.data.templates || []));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/pm/projects`, form);
      onCreated(data.id);
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed to create project');
    } finally { setSaving(false); }
  };

  const tpl = templates.find((t) => t.id === form.methodology);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="pm-new-modal">
      <form onSubmit={submit} className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <header className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="font-semibold">New implementation project</h3>
          <button type="button" onClick={onClose} className="text-slate-400"><X className="w-5 h-5" /></button>
        </header>
        <div className="p-5 space-y-3">
          <input required minLength={2} value={form.customer_org} onChange={(e) => setForm({ ...form, customer_org: e.target.value })} placeholder="Customer organisation (e.g. HSBC UK)" data-testid="pm-new-customer" className="w-full px-3 py-2 border rounded text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <input required minLength={2} value={form.client_owner_name} onChange={(e) => setForm({ ...form, client_owner_name: e.target.value })} placeholder="Client owner name" data-testid="pm-new-client-name" className="px-3 py-2 border rounded text-sm" />
            <input required type="email" value={form.client_owner_email} onChange={(e) => setForm({ ...form, client_owner_email: e.target.value })} placeholder="Client owner email" data-testid="pm-new-client-email" className="px-3 py-2 border rounded text-sm" />
            <input required minLength={2} value={form.vh_owner_name} onChange={(e) => setForm({ ...form, vh_owner_name: e.target.value })} placeholder="Vault Hire owner name" data-testid="pm-new-vh-name" className="px-3 py-2 border rounded text-sm" />
            <input required type="email" value={form.vh_owner_email} onChange={(e) => setForm({ ...form, vh_owner_email: e.target.value })} placeholder="Vault Hire owner email" data-testid="pm-new-vh-email" className="px-3 py-2 border rounded text-sm" />
          </div>
          <label className="block text-sm font-medium">Methodology</label>
          <div className="grid grid-cols-3 gap-2">
            {['waterfall', 'agile', 'wagile'].map((m) => (
              <button key={m} type="button" onClick={() => setForm({ ...form, methodology: m })}
                data-testid={`pm-new-methodology-${m}`}
                className={`px-3 py-2 rounded text-sm border capitalize ${form.methodology === m ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200'}`}>
                {m}
              </button>
            ))}
          </div>
          {tpl && (
            <div className="bg-slate-50 border rounded p-3 text-xs">
              <p className="font-medium text-slate-700 mb-1">{tpl.name}</p>
              <p className="text-slate-500 mb-2">{tpl.description}</p>
              <p className="text-slate-600">Phases: {tpl.phases.map((p) => p.name).join(' → ')}</p>
            </div>
          )}
          <input type="date" value={form.target_go_live} onChange={(e) => setForm({ ...form, target_go_live: e.target.value })} placeholder="Target go-live" data-testid="pm-new-golive" className="w-full px-3 py-2 border rounded text-sm" />
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (optional)" rows={2} className="w-full px-3 py-2 border rounded text-sm" />
        </div>
        <footer className="px-5 py-3 border-t flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600">Cancel</button>
          <button type="submit" disabled={saving} data-testid="pm-new-submit" className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded disabled:opacity-50">{saving ? 'Creating…' : 'Create project'}</button>
        </footer>
      </form>
    </div>
  );
}

// ===========================================================================
// DETAIL
// ===========================================================================
export function PMProjectDetail({ adminMode }) {
  const { projectId } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [project, setProject] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const [p, d] = await Promise.all([
        axios.get(`${API}/pm/projects/${projectId}`),
        axios.get(`${API}/pm/projects/${projectId}/dashboard`),
      ]);
      setProject(p.data);
      setDashboard(d.data);
    } catch (err) { logger.error('pm detail', err); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { reload(); }, [reload]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (!project) return <div className="min-h-screen flex items-center justify-center text-slate-500">Project not found.</div>;

  const isAdmin = project.access === 'admin';

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <Link to={adminMode ? '/admin/pm' : '/pm'} className="text-xs text-blue-600 hover:underline">← All projects</Link>
            <h1 className="text-xl font-bold mt-1" data-testid="pm-detail-title">{project.customer_org}</h1>
            <p className="text-xs text-slate-500 capitalize">{project.methodology} · {project.status} · {isAdmin ? 'Admin view' : 'Client view'}</p>
          </div>
          {dashboard && (
            <div className="text-right">
              <div className="flex items-center gap-2 justify-end mb-1">
                <span className={`w-2 h-2 rounded-full ${RAG_COLOR[dashboard.rag].dot}`} />
                <span className={`text-xs px-2 py-0.5 rounded ${RAG_COLOR[dashboard.rag].bg} ${RAG_COLOR[dashboard.rag].text}`} data-testid="pm-rag-badge">RAG: {dashboard.rag.toUpperCase()}</span>
              </div>
              <p className="text-2xl font-bold" data-testid="pm-percent-complete">{dashboard.percent_complete}%</p>
              <p className="text-xs text-slate-500">{dashboard.phase_complete}/{dashboard.phase_total} phases · {dashboard.task_overdue} overdue</p>
            </div>
          )}
        </div>
        <nav className="container mx-auto px-4 pb-1 flex gap-1 flex-wrap">
          {[
            ['overview', 'Overview'], ['phases', 'Phases & Exit Criteria'],
            ['scope', 'Scope & Contract'],
            ['tasks', 'Tasks'], ['alerts', 'Alerts'],
            ['issues', 'Issue Log'],
            ['chase', 'Chase'], ['audit', 'Audit'],
          ].map(([id, label]) => (
            <button key={id} onClick={() => setActiveTab(id)} data-testid={`pm-tab-${id}`}
              className={`px-3 py-2 text-sm rounded-t-md border-b-2 transition ${activeTab === id ? 'border-blue-600 text-blue-700 font-medium' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
              {label}
            </button>
          ))}
        </nav>
      </header>

      <main className="container mx-auto px-4 py-6">
        {activeTab === 'overview' && <OverviewTab project={project} dashboard={dashboard} isAdmin={isAdmin} onAdvance={reload} />}
        {activeTab === 'phases' && <PhasesTab project={project} isAdmin={isAdmin} onChange={reload} />}
        {activeTab === 'scope' && <ScopeTab projectId={projectId} isAdmin={isAdmin} />}
        {activeTab === 'tasks' && <TasksTab projectId={projectId} project={project} isAdmin={isAdmin} />}
        {activeTab === 'alerts' && <AlertsTab projectId={projectId} />}
        {activeTab === 'issues' && <IssuesTab projectId={projectId} />}
        {activeTab === 'chase' && <ChaseTab projectId={projectId} isAdmin={isAdmin} />}
        {activeTab === 'audit' && <AuditTab projectId={projectId} />}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
function OverviewTab({ project, dashboard, isAdmin, onAdvance }) {
  const advance = async () => {
    if (!window.confirm('Advance to the next phase? This requires all current-phase exit criteria to be met.')) return;
    try {
      const { data } = await axios.post(`${API}/pm/projects/${project.id}/advance-phase`);
      window.alert(data.completed ? 'Project marked complete!' : `Advanced to ${data.next_phase}`);
      onAdvance();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Cannot advance');
    }
  };

  return (
    <div data-testid="pm-overview">
      <div className="grid md:grid-cols-4 gap-3 mb-5">
        <Stat label="Phases complete" value={`${dashboard.phase_complete}/${dashboard.phase_total}`} />
        <Stat label="Tasks done" value={`${dashboard.task_done}/${dashboard.task_total}`} />
        <Stat label="Overdue tasks" value={dashboard.task_overdue} highlight={dashboard.task_overdue > 0 ? 'red' : null} />
        <Stat label="Open issues (P0/P1)" value={`${dashboard.issue_p0} / ${dashboard.issue_p1}`} highlight={dashboard.issue_p0 > 0 ? 'red' : null} />
      </div>

      <div className="bg-white border rounded p-5 mb-4">
        <h3 className="font-semibold mb-3">Phase progress</h3>
        <ol className="space-y-2">
          {project.phases.map((p) => {
            const rag = RAG_COLOR[p.rag] || RAG_COLOR.grey;
            return (
              <li key={p.id} className="flex items-center gap-3" data-testid={`pm-phase-row-${p.id}`}>
                {p.status === 'complete' ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  : p.status === 'in_progress' ? <Activity className="w-4 h-4 text-blue-500 shrink-0" />
                  : <Circle className="w-4 h-4 text-slate-300 shrink-0" />}
                <span className="flex-1 text-sm">{p.name}</span>
                <span className="text-xs text-slate-500">{p.exit_criteria_met}/{p.exit_criteria_total} criteria</span>
                <span className={`text-xs px-2 py-0.5 rounded ${rag.bg} ${rag.text}`}>{p.status}</span>
              </li>
            );
          })}
        </ol>
        {isAdmin && project.status === 'active' && (
          <button onClick={advance} data-testid="pm-advance-phase-btn" className="mt-4 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded inline-flex items-center gap-1">
            Advance to next phase <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-3 text-sm">
        <div className="bg-white border rounded p-4">
          <p className="text-xs text-slate-500 mb-1">Client owner</p>
          <p className="font-medium">{project.client_owner_name}</p>
          <p className="text-xs text-slate-500">{project.client_owner_email}</p>
        </div>
        <div className="bg-white border rounded p-4">
          <p className="text-xs text-slate-500 mb-1">Vault Hire owner</p>
          <p className="font-medium">{project.vh_owner_name}</p>
          <p className="text-xs text-slate-500">{project.vh_owner_email}</p>
        </div>
      </div>

      {isAdmin && <AISummaryPanel projectId={project.id} />}
    </div>
  );
}

function Stat({ label, value, highlight }) {
  const color = highlight === 'red' ? 'text-rose-700' : 'text-slate-900';
  return (
    <div className="bg-white border rounded p-4">
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
function PhasesTab({ project, isAdmin, onChange }) {
  const toggle = async (phaseId, ecId, current) => {
    try {
      await axios.post(`${API}/pm/projects/${project.id}/phases/${phaseId}/exit-criteria/${ecId}`, {
        met: !current,
      });
      onChange();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="pm-phases-tab">
      {project.phases.map((ph) => (
        <div key={ph.id} className="bg-white border rounded p-4" data-testid={`pm-phase-${ph.id}`}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-semibold">{ph.name}</h3>
              <p className="text-xs text-slate-500 capitalize">{ph.status}</p>
            </div>
            <span className="text-xs text-slate-500">{ph.exit_criteria_met}/{ph.exit_criteria_total} met</span>
          </div>
          <ul className="space-y-1.5">
            {ph.exit_criteria.map((ec) => (
              <li key={ec.id} className="flex items-start gap-2 text-sm" data-testid={`pm-ec-${ec.id}`}>
                <input
                  type="checkbox"
                  checked={ec.met}
                  disabled={!isAdmin && project.access !== 'client'}
                  onChange={() => toggle(ph.id, ec.id, ec.met)}
                  data-testid={`pm-ec-check-${ec.id}`}
                  className="mt-1"
                />
                <div className="flex-1">
                  <p className={ec.met ? 'line-through text-slate-500' : ''}>{ec.label}</p>
                  {ec.met_at && <p className="text-xs text-emerald-700">Met {ec.met_at.slice(0, 10)}</p>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
function TasksTab({ projectId, project, isAdmin }) {
  const [tasks, setTasks] = useState([]);
  const [scopeItems, setScopeItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState({ phase_id: '', status: '' });
  const [addSubFor, setAddSubFor] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (filter.phase_id) params.set('phase_id', filter.phase_id);
    if (filter.status) params.set('status', filter.status);
    const { data } = await axios.get(`${API}/pm/projects/${projectId}/tasks?${params}`);
    setTasks(data || []);
    try {
      const sc = await axios.get(`${API}/pm/projects/${projectId}/scope`);
      setScopeItems(sc.data.items || []);
    } catch (e) { /* clients may 403 on /scope — fine */ }
  }, [projectId, filter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (taskId, status) => {
    try {
      await axios.put(`${API}/pm/projects/${projectId}/tasks/${taskId}`, { status });
      load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed');
    }
  };

  const remove = async (task) => {
    const childCount = tasks.filter((t) => t.parent_task_id === task.id).length;
    const msg = childCount
      ? `Delete "${task.title}" AND its ${childCount} sub-task(s)? This cannot be undone.`
      : `Delete "${task.title}"? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/pm/projects/${projectId}/tasks/${task.id}`);
      load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed');
    }
  };

  // Group: top-level tasks (parent_task_id null/undefined), then nest children
  const topLevel = tasks.filter((t) => !t.parent_task_id);
  const childrenOf = (id) => tasks.filter((t) => t.parent_task_id === id);

  const renderTaskRow = (t, isChild = false) => {
    const overdue = t.due_date && t.due_date < new Date().toISOString() && !['done', 'cancelled'].includes(t.status);
    const depTasks = (t.depends_on || []).map((id) => tasks.find((x) => x.id === id)).filter(Boolean);
    const scopeItem = t.scope_item_id ? scopeItems.find((s) => s.id === t.scope_item_id) : null;
    const subs = childrenOf(t.id);
    return (
      <li key={t.id} data-testid={`pm-task-${t.id}`}
          className={`bg-white border rounded p-3 ${isChild ? 'ml-6 border-l-4 border-l-blue-200' : ''}`}>
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <span className={`text-xs px-2 py-0.5 rounded uppercase ${t.owner_side === 'vh' ? 'bg-purple-100 text-purple-700' : 'bg-cyan-100 text-cyan-700'}`}>{t.owner_side}</span>
              <span className={`text-xs px-2 py-0.5 rounded ${TASK_STATUS_COLOR[t.status]}`}>{t.status}</span>
              {t.priority !== 'medium' && <span className="text-xs text-slate-500">{t.priority}</span>}
              {overdue && <span className="text-xs bg-rose-100 text-rose-700 px-2 py-0.5 rounded" data-testid={`pm-task-overdue-${t.id}`}>Overdue</span>}
              {t.is_out_of_scope && <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded" data-testid={`pm-task-oos-${t.id}`}>Out of scope</span>}
              {scopeItem && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">{scopeItem.label}</span>}
              {depTasks.length > 0 && <span className="text-xs text-amber-700">▶ depends on {depTasks.length}</span>}
            </div>
            <p className="font-medium text-sm">{t.title}</p>
            {t.description && <p className="text-xs text-slate-600 mt-0.5">{t.description}</p>}
            <p className="text-xs text-slate-500 mt-1">{t.owner_email}{t.due_date && ` · due ${t.due_date.slice(0, 10)}`}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <select value={t.status} onChange={(e) => updateStatus(t.id, e.target.value)} data-testid={`pm-task-status-${t.id}`} className="text-xs px-2 py-1 border rounded">
              {['todo', 'in_progress', 'blocked', 'done', 'cancelled'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {!isChild && isAdmin && (
              <button onClick={() => setAddSubFor(t)} title="Add sub-task" data-testid={`pm-task-add-sub-${t.id}`} className="text-xs px-2 py-1 border rounded text-slate-600 hover:bg-slate-50">+ sub</button>
            )}
            {isAdmin && (
              <button onClick={() => setEditing(t)} title="Edit" data-testid={`pm-task-edit-${t.id}`} className="text-xs px-2 py-1 border rounded text-slate-600 hover:bg-slate-50">Edit</button>
            )}
            {isAdmin && (
              <button onClick={() => remove(t)} title="Delete" data-testid={`pm-task-delete-${t.id}`} className="text-xs px-2 py-1 border rounded text-rose-600 hover:bg-rose-50">Del</button>
            )}
          </div>
        </div>
        {subs.length > 0 && (
          <ul className="mt-2 space-y-2" data-testid={`pm-task-subs-${t.id}`}>
            {subs.map((c) => renderTaskRow(c, true))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <div data-testid="pm-tasks-tab">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select value={filter.phase_id} onChange={(e) => setFilter({ ...filter, phase_id: e.target.value })} data-testid="pm-tasks-phase-filter" className="px-3 py-1.5 border rounded text-sm">
          <option value="">All phases</option>
          {project.phases.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} data-testid="pm-tasks-status-filter" className="px-3 py-1.5 border rounded text-sm">
          <option value="">All statuses</option>
          {['todo', 'in_progress', 'blocked', 'done', 'cancelled'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={() => setShowNew(true)} data-testid="pm-new-task-btn" className="ml-auto px-3 py-1.5 bg-blue-600 text-white text-sm rounded inline-flex items-center gap-1">
          <Plus className="w-4 h-4" /> New task
        </button>
      </div>

      {showNew && <NewTaskModal projectId={projectId} project={project} tasks={tasks} scopeItems={scopeItems} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      {addSubFor && <NewTaskModal projectId={projectId} project={project} tasks={tasks} scopeItems={scopeItems} parentTask={addSubFor} onClose={() => setAddSubFor(null)} onCreated={() => { setAddSubFor(null); load(); }} />}
      {editing && <EditTaskModal projectId={projectId} project={project} tasks={tasks} scopeItems={scopeItems} task={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      {topLevel.length === 0 ? <p className="text-center text-slate-400 py-12">No tasks match.</p> :
        <ul className="space-y-2" data-testid="pm-tasks-list">
          {topLevel.map((t) => renderTaskRow(t, false))}
        </ul>
      }
    </div>
  );
}

function NewTaskModal({ projectId, project, tasks = [], scopeItems = [], parentTask = null, onClose, onCreated }) {
  const [form, setForm] = useState({
    phase_id: parentTask?.phase_id || project.current_phase_id || project.phases[0]?.id || '',
    title: '', description: '', owner_side: parentTask?.owner_side || 'vh', owner_email: '',
    due_date: '', priority: 'medium',
    depends_on: [], scope_item_id: '', is_out_of_scope: false,
  });
  const [saving, setSaving] = useState(false);

  const candidatesForDeps = tasks.filter((t) => !t.parent_task_id);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (parentTask) payload.parent_task_id = parentTask.id;
      if (!payload.due_date) delete payload.due_date;
      if (!payload.description) delete payload.description;
      if (!payload.scope_item_id) delete payload.scope_item_id;
      if (!payload.depends_on || payload.depends_on.length === 0) delete payload.depends_on;
      await axios.post(`${API}/pm/projects/${projectId}/tasks`, payload);
      onCreated();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const toggleDep = (id) => {
    setForm((f) => ({
      ...f,
      depends_on: f.depends_on.includes(id) ? f.depends_on.filter((x) => x !== id) : [...f.depends_on, id],
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="pm-new-task-modal">
      <form onSubmit={submit} className="bg-white rounded-lg max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <header className="px-5 py-4 border-b">
          <h3 className="font-semibold">{parentTask ? `New sub-task under "${parentTask.title}"` : 'New task'}</h3>
        </header>
        <div className="p-5 space-y-2">
          <select required disabled={!!parentTask} value={form.phase_id} onChange={(e) => setForm({ ...form, phase_id: e.target.value })} data-testid="pm-task-phase" className="w-full px-3 py-2 border rounded text-sm disabled:bg-slate-50">
            {project.phases.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <input required minLength={2} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Task title" data-testid="pm-task-title" className="w-full px-3 py-2 border rounded text-sm" />
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (optional)" rows={2} className="w-full px-3 py-2 border rounded text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <select disabled={!!parentTask} value={form.owner_side} onChange={(e) => setForm({ ...form, owner_side: e.target.value })} data-testid="pm-task-side" className="px-3 py-2 border rounded text-sm disabled:bg-slate-50">
              <option value="vh">Vault Hire</option>
              <option value="client">Client</option>
            </select>
            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="px-3 py-2 border rounded text-sm">
              {['low', 'medium', 'high', 'critical'].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <input required type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} placeholder="Owner email" data-testid="pm-task-owner-email" className="px-3 py-2 border rounded text-sm col-span-2" />
            <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} placeholder="Due date" data-testid="pm-task-due" className="px-3 py-2 border rounded text-sm col-span-2" />
          </div>

          {/* Scope linkage */}
          {scopeItems.length > 0 && !parentTask && (
            <div className="border-t pt-3 mt-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1">Scope linkage</label>
              <select value={form.scope_item_id} onChange={(e) => setForm({ ...form, scope_item_id: e.target.value })} data-testid="pm-task-scope-item" className="w-full px-3 py-2 border rounded text-sm">
                <option value="">— Not categorised —</option>
                {scopeItems.filter((s) => s.in_scope).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <label className="flex items-center gap-2 mt-2 text-xs text-amber-800">
                <input type="checkbox" checked={form.is_out_of_scope} onChange={(e) => setForm({ ...form, is_out_of_scope: e.target.checked, scope_item_id: e.target.checked ? '' : form.scope_item_id })} data-testid="pm-task-oos-checkbox" />
                <span>Flag this task as <strong>out of scope</strong> (raises a scope-creep alert)</span>
              </label>
            </div>
          )}

          {/* Dependencies */}
          {candidatesForDeps.length > 0 && !parentTask && (
            <div className="border-t pt-3 mt-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1">Depends on (must complete first)</label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 bg-slate-50" data-testid="pm-task-deps-list">
                {candidatesForDeps.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-xs py-0.5">
                    <input type="checkbox" checked={form.depends_on.includes(t.id)} onChange={() => toggleDep(t.id)} />
                    <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase ${t.owner_side === 'vh' ? 'bg-purple-100 text-purple-700' : 'bg-cyan-100 text-cyan-700'}`}>{t.owner_side}</span>
                    <span className="truncate">{t.title}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <footer className="px-5 py-3 border-t flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600">Cancel</button>
          <button type="submit" disabled={saving} data-testid="pm-task-submit" className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded disabled:opacity-50">Create</button>
        </footer>
      </form>
    </div>
  );
}

function EditTaskModal({ projectId, project, tasks, scopeItems, task, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: task.title || '',
    description: task.description || '',
    owner_email: task.owner_email || '',
    due_date: task.due_date ? task.due_date.slice(0, 10) : '',
    priority: task.priority || 'medium',
    depends_on: task.depends_on || [],
    scope_item_id: task.scope_item_id || '',
    is_out_of_scope: !!task.is_out_of_scope,
  });
  const [saving, setSaving] = useState(false);

  const candidatesForDeps = tasks.filter((t) => t.id !== task.id && !t.parent_task_id);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description || null,
        owner_email: form.owner_email,
        due_date: form.due_date || null,
        priority: form.priority,
        depends_on: form.depends_on,
        scope_item_id: form.scope_item_id || null,
        is_out_of_scope: form.is_out_of_scope,
      };
      await axios.put(`${API}/pm/projects/${projectId}/tasks/${task.id}`, payload);
      onSaved();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const toggleDep = (id) => {
    setForm((f) => ({
      ...f,
      depends_on: f.depends_on.includes(id) ? f.depends_on.filter((x) => x !== id) : [...f.depends_on, id],
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="pm-edit-task-modal">
      <form onSubmit={submit} className="bg-white rounded-lg max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <header className="px-5 py-4 border-b"><h3 className="font-semibold">Edit task</h3></header>
        <div className="p-5 space-y-2">
          <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" data-testid="pm-edit-task-title" />
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full px-3 py-2 border rounded text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <input type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} className="px-3 py-2 border rounded text-sm col-span-2" />
            <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="px-3 py-2 border rounded text-sm" />
            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="px-3 py-2 border rounded text-sm">
              {['low', 'medium', 'high', 'critical'].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          {!task.parent_task_id && scopeItems.length > 0 && (
            <div className="border-t pt-3 mt-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1">Scope linkage</label>
              <select value={form.scope_item_id} onChange={(e) => setForm({ ...form, scope_item_id: e.target.value })} className="w-full px-3 py-2 border rounded text-sm">
                <option value="">— Not categorised —</option>
                {scopeItems.filter((s) => s.in_scope).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <label className="flex items-center gap-2 mt-2 text-xs text-amber-800">
                <input type="checkbox" checked={form.is_out_of_scope} onChange={(e) => setForm({ ...form, is_out_of_scope: e.target.checked, scope_item_id: e.target.checked ? '' : form.scope_item_id })} />
                <span>Flag as <strong>out of scope</strong></span>
              </label>
            </div>
          )}
          {!task.parent_task_id && candidatesForDeps.length > 0 && (
            <div className="border-t pt-3 mt-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1">Depends on</label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 bg-slate-50">
                {candidatesForDeps.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-xs py-0.5">
                    <input type="checkbox" checked={form.depends_on.includes(t.id)} onChange={() => toggleDep(t.id)} />
                    <span className="truncate">{t.title}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <footer className="px-5 py-3 border-t flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600">Cancel</button>
          <button type="submit" disabled={saving} data-testid="pm-edit-task-submit" className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded disabled:opacity-50">Save changes</button>
        </footer>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
function IssuesTab({ projectId }) {
  const [issues, setIssues] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (filter) params.set('status', filter);
    const { data } = await axios.get(`${API}/pm/projects/${projectId}/issues?${params}`);
    setIssues(data || []);
  }, [projectId, filter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    const notes = status === 'resolved' ? window.prompt('Resolution notes (optional):') : null;
    try {
      await axios.put(`${API}/pm/projects/${projectId}/issues/${id}`, { status, resolution_notes: notes });
      load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div data-testid="pm-issues-tab">
      <div className="flex items-center gap-2 mb-4">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="pm-issues-filter" className="px-3 py-1.5 border rounded text-sm">
          <option value="">All statuses</option>
          {['open', 'in_progress', 'resolved', 'closed'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={() => setShowNew(true)} data-testid="pm-new-issue-btn" className="ml-auto px-3 py-1.5 bg-rose-600 text-white text-sm rounded inline-flex items-center gap-1">
          <ShieldAlert className="w-4 h-4" /> Log issue
        </button>
      </div>

      {showNew && <NewIssueModal projectId={projectId} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}

      {issues.length === 0 ? <p className="text-center text-slate-400 py-12">No issues logged.</p> :
        <ul className="space-y-2" data-testid="pm-issues-list">
          {issues.map((i) => (
            <li key={i.id} data-testid={`pm-issue-${i.id}`} className="bg-white border rounded p-3">
              <div className="flex items-start gap-2 mb-1">
                <span className={`text-xs px-2 py-0.5 rounded ${SEVERITY_COLOR[i.severity]}`}>{i.severity}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${i.status === 'open' ? 'bg-amber-100 text-amber-800' : i.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{i.status}</span>
                <span className={`text-xs px-2 py-0.5 rounded uppercase ${i.owner_side === 'vh' ? 'bg-purple-100 text-purple-700' : 'bg-cyan-100 text-cyan-700'}`}>{i.owner_side}</span>
                <span className="text-xs text-slate-500 ml-auto">{i.created_at?.slice(0, 10)}</span>
              </div>
              <p className="font-medium text-sm">{i.title}</p>
              {i.description && <p className="text-xs text-slate-600 mt-1">{i.description}</p>}
              <div className="mt-2 flex items-center gap-2">
                <select value={i.status} onChange={(e) => updateStatus(i.id, e.target.value)} data-testid={`pm-issue-status-${i.id}`} className="text-xs px-2 py-1 border rounded">
                  {['open', 'in_progress', 'resolved', 'closed'].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                {i.resolution_notes && <span className="text-xs text-slate-500 italic">"{i.resolution_notes}"</span>}
              </div>
            </li>
          ))}
        </ul>
      }
    </div>
  );
}

function NewIssueModal({ projectId, onClose, onCreated }) {
  const [form, setForm] = useState({ title: '', description: '', severity: 'P2', owner_side: 'vh', owner_email: '' });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.description) delete payload.description;
      await axios.post(`${API}/pm/projects/${projectId}/issues`, payload);
      onCreated();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="pm-new-issue-modal">
      <form onSubmit={submit} className="bg-white rounded-lg max-w-xl w-full">
        <header className="px-5 py-4 border-b"><h3 className="font-semibold">Log issue</h3></header>
        <div className="p-5 space-y-2">
          <input required minLength={2} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Issue title" data-testid="pm-issue-title" className="w-full px-3 py-2 border rounded text-sm" />
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (optional)" rows={3} className="w-full px-3 py-2 border rounded text-sm" />
          <div className="grid grid-cols-3 gap-2">
            <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} data-testid="pm-issue-severity" className="px-3 py-2 border rounded text-sm">
              {['P0', 'P1', 'P2', 'P3'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={form.owner_side} onChange={(e) => setForm({ ...form, owner_side: e.target.value })} className="px-3 py-2 border rounded text-sm">
              <option value="vh">Vault Hire</option>
              <option value="client">Client</option>
            </select>
            <input required type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} placeholder="Owner email" className="px-3 py-2 border rounded text-sm" />
          </div>
        </div>
        <footer className="px-5 py-3 border-t flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600">Cancel</button>
          <button type="submit" disabled={saving} data-testid="pm-issue-submit" className="px-3 py-1.5 bg-rose-600 text-white text-sm rounded disabled:opacity-50">Log issue</button>
        </footer>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ChaseTab({ projectId, isAdmin }) {
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/pm/projects/${projectId}/chase-log`);
    setHistory(data || []);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/pm/projects/${projectId}/chase`);
      setLastResult(data);
      load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed');
    } finally { setRunning(false); }
  };

  return (
    <div data-testid="pm-chase-tab">
      <div className="bg-white border rounded p-5 mb-4">
        <h3 className="font-semibold mb-2 flex items-center gap-2"><Bell className="w-4 h-4 text-blue-600" /> Run chase on overdue tasks</h3>
        <p className="text-sm text-slate-600 mb-3">Finds all tasks past their due date and (in production) emails the owner. Currently mocked — chase events are logged for audit.</p>
        {isAdmin && (
          <button onClick={run} disabled={running} data-testid="pm-run-chase-btn" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded disabled:opacity-50 inline-flex items-center gap-1">
            <Send className="w-4 h-4" /> {running ? 'Running…' : 'Run chase'}
          </button>
        )}
        {lastResult && (
          <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded text-sm" data-testid="pm-chase-last-result">
            Found <strong>{lastResult.overdue_count}</strong> overdue task{lastResult.overdue_count === 1 ? '' : 's'}.
            {lastResult.overdue_count > 0 && <span className="text-xs text-slate-600"> Mocked emails dispatched.</span>}
          </div>
        )}
      </div>

      <div className="bg-white border rounded p-5">
        <h3 className="font-semibold mb-3">Chase history</h3>
        {history.length === 0 ? <p className="text-sm text-slate-400">No chase runs yet.</p> :
          <ul className="space-y-2" data-testid="pm-chase-history">
            {history.map((h) => (
              <li key={h.id} className="text-sm border rounded p-2">
                <div className="flex items-center justify-between">
                  <span>{h.run_at?.slice(0, 16).replace('T', ' ')}</span>
                  <span className="text-xs text-slate-500">{h.overdue_count} overdue</span>
                </div>
              </li>
            ))}
          </ul>
        }
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function AuditTab({ projectId }) {
  const [entries, setEntries] = useState([]);
  useEffect(() => {
    axios.get(`${API}/pm/projects/${projectId}/audit`)
      .then((r) => setEntries(r.data || []))
      .catch(() => {});
  }, [projectId]);
  return (
    <div data-testid="pm-audit-tab" className="bg-white border rounded p-5">
      <h3 className="font-semibold mb-3">Audit log</h3>
      {entries.length === 0 ? <p className="text-sm text-slate-400">No audit entries yet.</p> :
        <ul className="space-y-1 text-sm">
          {entries.map((e) => (
            <li key={e.id} data-testid={`pm-audit-${e.id}`} className="border-b py-2 text-xs">
              <span className="text-slate-500">{e.at?.slice(0, 16).replace('T', ' ')}</span>{' · '}
              <span className="font-mono">{e.action}</span>
              {e.payload && Object.keys(e.payload).length > 0 && (
                <span className="text-slate-500"> · {JSON.stringify(e.payload).slice(0, 200)}</span>
              )}
            </li>
          ))}
        </ul>
      }
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scope & Contract tab (Phase 2)
// ---------------------------------------------------------------------------
function ScopeTab({ projectId, isAdmin }) {
  const [scope, setScope] = useState(null);
  const [form, setForm] = useState({ scope_document_url: '', scope_summary: '', contract_value: '', contract_signed_date: '' });
  const [newItem, setNewItem] = useState({ label: '', description: '', in_scope: true });
  const [savingDoc, setSavingDoc] = useState(false);
  const [savingItem, setSavingItem] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/pm/projects/${projectId}/scope`);
      setScope(data);
      setForm({
        scope_document_url: data.scope_document_url || '',
        scope_summary: data.scope_summary || '',
        contract_value: data.contract_value || '',
        contract_signed_date: data.contract_signed_date || '',
      });
    } catch (e) { /* no-op */ }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const saveDoc = async (e) => {
    e.preventDefault();
    setSavingDoc(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([_, v]) => v !== ''));
      await axios.put(`${API}/pm/projects/${projectId}/scope`, payload);
      await load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed');
    } finally { setSavingDoc(false); }
  };

  const addItem = async (e) => {
    e.preventDefault();
    setSavingItem(true);
    try {
      await axios.post(`${API}/pm/projects/${projectId}/scope/items`, newItem);
      setNewItem({ label: '', description: '', in_scope: true });
      await load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed');
    } finally { setSavingItem(false); }
  };

  const removeItem = async (id) => {
    if (!window.confirm('Remove this scope item? Any tasks linked to it will be unlinked.')) return;
    try {
      await axios.delete(`${API}/pm/projects/${projectId}/scope/items/${id}`);
      load();
    } catch (err) { window.alert(err.response?.data?.detail || 'Failed'); }
  };

  if (!scope) return <p className="text-slate-400 text-sm" data-testid="pm-scope-loading">Loading…</p>;
  const inScope = scope.items.filter((i) => i.in_scope);
  const exclusions = scope.items.filter((i) => !i.in_scope);

  return (
    <div className="space-y-6" data-testid="pm-scope-tab">
      <section className="bg-white border rounded p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Signed scope &amp; contract</h3>
        {isAdmin ? (
          <form onSubmit={saveDoc} className="space-y-2">
            <input type="url" value={form.scope_document_url} onChange={(e) => setForm({ ...form, scope_document_url: e.target.value })} placeholder="Signed SOW document URL (e.g. SharePoint, Drive)" data-testid="pm-scope-doc-url" className="w-full px-3 py-2 border rounded text-sm" />
            <textarea value={form.scope_summary} onChange={(e) => setForm({ ...form, scope_summary: e.target.value })} placeholder="Brief written summary of the scope (this becomes the audit baseline)" rows={3} data-testid="pm-scope-summary" className="w-full px-3 py-2 border rounded text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <input type="text" value={form.contract_value} onChange={(e) => setForm({ ...form, contract_value: e.target.value })} placeholder="Contract value (e.g. £250,000)" data-testid="pm-scope-contract-value" className="px-3 py-2 border rounded text-sm" />
              <input type="date" value={form.contract_signed_date} onChange={(e) => setForm({ ...form, contract_signed_date: e.target.value })} placeholder="Contract signed date" data-testid="pm-scope-signed-date" className="px-3 py-2 border rounded text-sm" />
            </div>
            <button type="submit" disabled={savingDoc} data-testid="pm-scope-save" className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded disabled:opacity-50">
              {savingDoc ? 'Saving…' : 'Save contract details'}
            </button>
          </form>
        ) : (
          <div className="text-sm space-y-1">
            <p><span className="text-slate-500">Document:</span> {scope.scope_document_url ? <a href={scope.scope_document_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Open SOW</a> : <span className="text-slate-400">Not yet attached</span>}</p>
            <p><span className="text-slate-500">Summary:</span> {scope.scope_summary || <span className="text-slate-400">—</span>}</p>
            <p><span className="text-slate-500">Contract value:</span> {scope.contract_value || <span className="text-slate-400">—</span>}</p>
            <p><span className="text-slate-500">Signed on:</span> {scope.contract_signed_date || <span className="text-slate-400">—</span>}</p>
          </div>
        )}
      </section>

      <section className="bg-white border rounded p-5">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700">In-scope items <span className="text-slate-400 font-normal">({inScope.length})</span></h3>
          <p className="text-xs text-slate-400">Every task should link to one of these — otherwise it's flagged as uncategorised in Alerts.</p>
        </div>
        {inScope.length === 0 ? (
          <p className="text-sm text-slate-400 mb-3" data-testid="pm-scope-empty-in">No in-scope items yet. Add them so tasks can be categorised against the contract.</p>
        ) : (
          <ul className="space-y-1 mb-3" data-testid="pm-scope-in-list">
            {inScope.map((i) => (
              <li key={i.id} data-testid={`pm-scope-item-${i.id}`} className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-emerald-900">{i.label}</p>
                  {i.description && <p className="text-xs text-emerald-800/70">{i.description}</p>}
                  <p className="text-xs text-emerald-700 mt-0.5">{i.task_count} task{i.task_count === 1 ? '' : 's'} linked</p>
                </div>
                {isAdmin && <button onClick={() => removeItem(i.id)} className="text-xs text-rose-600 hover:underline">Remove</button>}
              </li>
            ))}
          </ul>
        )}

        <div className="flex items-baseline justify-between mb-3 mt-5">
          <h3 className="text-sm font-semibold text-slate-700">Explicit exclusions <span className="text-slate-400 font-normal">({exclusions.length})</span></h3>
          <p className="text-xs text-slate-400">Things specifically NOT in scope (useful when the client tries to slip something in).</p>
        </div>
        {exclusions.length === 0 ? (
          <p className="text-sm text-slate-400 mb-3" data-testid="pm-scope-empty-out">No explicit exclusions.</p>
        ) : (
          <ul className="space-y-1 mb-3" data-testid="pm-scope-out-list">
            {exclusions.map((i) => (
              <li key={i.id} data-testid={`pm-scope-item-${i.id}`} className="flex items-center justify-between bg-rose-50 border border-rose-200 rounded px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-rose-900">{i.label}</p>
                  {i.description && <p className="text-xs text-rose-800/70">{i.description}</p>}
                </div>
                {isAdmin && <button onClick={() => removeItem(i.id)} className="text-xs text-rose-600 hover:underline">Remove</button>}
              </li>
            ))}
          </ul>
        )}

        {isAdmin && (
          <form onSubmit={addItem} className="border-t pt-3 mt-2 space-y-2">
            <h4 className="text-xs font-semibold text-slate-600">Add a scope item</h4>
            <input required minLength={2} value={newItem.label} onChange={(e) => setNewItem({ ...newItem, label: e.target.value })} placeholder="Label (e.g. Candidate ATS module)" data-testid="pm-scope-new-label" className="w-full px-3 py-2 border rounded text-sm" />
            <textarea value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} placeholder="Description (optional)" rows={2} className="w-full px-3 py-2 border rounded text-sm" />
            <div className="flex items-center gap-3">
              <label className="text-xs flex items-center gap-2">
                <input type="radio" checked={newItem.in_scope} onChange={() => setNewItem({ ...newItem, in_scope: true })} data-testid="pm-scope-new-in-radio" /> In scope
              </label>
              <label className="text-xs flex items-center gap-2">
                <input type="radio" checked={!newItem.in_scope} onChange={() => setNewItem({ ...newItem, in_scope: false })} data-testid="pm-scope-new-out-radio" /> Explicit exclusion
              </label>
              <button type="submit" disabled={savingItem} data-testid="pm-scope-new-submit" className="ml-auto px-3 py-1.5 bg-blue-600 text-white text-sm rounded disabled:opacity-50">
                {savingItem ? 'Adding…' : 'Add item'}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Alerts tab (Phase 2)
// ---------------------------------------------------------------------------
function AlertsTab({ projectId }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/pm/projects/${projectId}/alerts`);
      setData(r.data);
    } catch (e) { /* no-op */ }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-slate-400 text-sm" data-testid="pm-alerts-loading">Loading…</p>;

  const Section = ({ title, items, tone, testid, emptyMsg }) => (
    <section className={`border rounded p-4 ${tone}`} data-testid={testid}>
      <h3 className="text-sm font-semibold mb-2 flex items-center justify-between">
        <span>{title}</span>
        <span className="text-xs font-normal opacity-70">{items.length}</span>
      </h3>
      {items.length === 0 ? (
        <p className="text-xs opacity-70">{emptyMsg}</p>
      ) : (
        <ul className="space-y-1">
          {items.slice(0, 50).map((t) => (
            <li key={t.id} className="text-sm flex items-center justify-between gap-2">
              <span className="truncate">{t.title}</span>
              {t.due_date && <span className="text-xs opacity-70 shrink-0">{t.due_date.slice(0, 10)}</span>}
              {t.blocked_by_title && <span className="text-xs opacity-70 shrink-0">⛔ {t.blocked_by_title}</span>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );

  return (
    <div data-testid="pm-alerts-tab" className="space-y-3">
      <p className="text-xs text-slate-500">Snapshot generated at {data.generated_at.slice(0, 16).replace('T', ' ')} UTC.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Section title="Overdue" items={data.overdue} testid="pm-alerts-overdue" emptyMsg="Nothing overdue." tone="bg-rose-50 border-rose-200 text-rose-900" />
        <Section title="At risk (due within 3 days)" items={data.at_risk} testid="pm-alerts-at-risk" emptyMsg="Nothing at risk." tone="bg-amber-50 border-amber-200 text-amber-900" />
        <Section title="Blocked by dependencies" items={data.blocked_by_dependencies} testid="pm-alerts-blocked" emptyMsg="No tasks are blocked." tone="bg-slate-50 border-slate-200 text-slate-800" />
        <Section title="Scope creep (out of scope)" items={data.scope_creep} testid="pm-alerts-scope-creep" emptyMsg="No out-of-scope tasks flagged." tone="bg-fuchsia-50 border-fuchsia-200 text-fuchsia-900" />
        <Section title="Uncategorised (no scope linkage)" items={data.uncategorised} testid="pm-alerts-uncategorised" emptyMsg="Every task is linked to a scope item." tone="bg-orange-50 border-orange-200 text-orange-900" />
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// AI Project Health Summary panel (Overview tab, admin only)
// ---------------------------------------------------------------------------
function AISummaryPanel({ projectId }) {
  const [generating, setGenerating] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/pm/projects/${projectId}/ai-summary/history`);
      setHistory(data || []);
      // Pre-fill panel with the most recent summary if user hasn't generated yet this session.
      if (!summary && data?.length) setSummary(data[0]);
    } catch (e) { /* no-op */ }
  }, [projectId, summary]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const generate = async () => {
    setGenerating(true); setError('');
    try {
      const { data } = await axios.post(`${API}/pm/projects/${projectId}/ai-summary`);
      setSummary(data);
      loadHistory();
    } catch (err) {
      setError(err.response?.data?.detail || 'AI summary failed. Please try again.');
    } finally { setGenerating(false); }
  };

  const copy = async (text, which) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(null), 1500);
    } catch (e) { /* ignore */ }
  };

  const ragTone = {
    green: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    red:   'bg-rose-50 border-rose-200 text-rose-900',
  };

  return (
    <section className="mt-5 bg-white border rounded p-5" data-testid="pm-ai-summary-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-semibold text-slate-900">AI project health summary</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Claude reads phases, tasks, scope, dependencies, and issues → drafts an internal exec brief AND a client-facing status note.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {history.length > 0 && (
            <button
              onClick={() => setShowHistory((s) => !s)}
              className="text-xs px-2 py-1.5 border rounded text-slate-600 hover:bg-slate-50"
              data-testid="pm-ai-summary-history-btn"
            >
              {showHistory ? 'Hide history' : `History (${history.length})`}
            </button>
          )}
          <button
            onClick={generate}
            disabled={generating}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="pm-ai-summary-generate-btn"
          >
            {generating ? 'Generating…' : (summary ? 'Regenerate' : 'Generate summary')}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm px-3 py-2 rounded mb-3" data-testid="pm-ai-summary-error">
          {error}
        </div>
      )}

      {!summary && !generating && (
        <p className="text-sm text-slate-400 py-6 text-center">No summary yet — click "Generate summary" to draft one.</p>
      )}

      {summary && (
        <div data-testid="pm-ai-summary-content" className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            {summary.rag && (
              <span className={`text-xs px-2 py-1 rounded border ${ragTone[summary.rag] || ragTone.amber}`} data-testid="pm-ai-summary-rag">
                Status: {String(summary.rag).toUpperCase()}
              </span>
            )}
            {summary.headline && <span className="text-sm font-medium text-slate-700" data-testid="pm-ai-summary-headline">{summary.headline}</span>}
            <span className="text-xs text-slate-400 ml-auto">Generated {summary.generated_at?.slice(0, 16).replace('T', ' ')} UTC</span>
          </div>

          {Array.isArray(summary.key_risks) && summary.key_risks.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3" data-testid="pm-ai-summary-risks">
              <p className="text-xs font-semibold text-amber-900 mb-1">Key risks</p>
              <ul className="list-disc pl-5 text-sm text-amber-900 space-y-0.5">
                {summary.key_risks.slice(0, 8).map((r, i) => (
                  <li key={i}>{typeof r === 'string' ? r : (r.risk || r.title || JSON.stringify(r))}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-3">
            <article className="border rounded p-3 bg-slate-50" data-testid="pm-ai-summary-internal">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold tracking-wide uppercase text-slate-500">Internal (Vault Hire exec)</h4>
                <button
                  onClick={() => copy(summary.internal_summary, 'internal')}
                  className="text-xs text-blue-600 hover:underline"
                  data-testid="pm-ai-summary-copy-internal"
                >
                  {copied === 'internal' ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{summary.internal_summary}</div>
            </article>

            <article className="border rounded p-3 bg-blue-50" data-testid="pm-ai-summary-external">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold tracking-wide uppercase text-blue-700">External (client-facing)</h4>
                <button
                  onClick={() => copy(summary.external_summary, 'external')}
                  className="text-xs text-blue-600 hover:underline"
                  data-testid="pm-ai-summary-copy-external"
                >
                  {copied === 'external' ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{summary.external_summary}</div>
            </article>
          </div>

          <p className="text-xs text-slate-400">
            AI-generated draft. Review and edit before sharing externally.
          </p>
        </div>
      )}

      {showHistory && history.length > 0 && (
        <div className="border-t pt-3 mt-4" data-testid="pm-ai-summary-history">
          <h4 className="text-xs font-semibold text-slate-600 mb-2">Previous summaries</h4>
          <ul className="space-y-1 text-sm">
            {history.map((h) => (
              <li key={h.id || h.generated_at}>
                <button
                  onClick={() => setSummary(h)}
                  className="text-blue-600 hover:underline text-xs"
                  data-testid={`pm-ai-summary-history-item-${h.generated_at}`}
                >
                  {h.generated_at?.slice(0, 16).replace('T', ' ')} UTC {h.headline ? `— ${h.headline}` : ''}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

